import React, { useReducer, useRef, useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PanelRightOpen, PanelRightClose, RotateCcw, Sparkles, Workflow, Loader2, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { PhaseOptionPicker } from './PhaseOptionPicker';
import { QuestionForm } from './QuestionForm';
import { TemplateCarousel } from './TemplateCarousel';
import { PhaseTracker } from './PhaseTracker';
import { MiniDiagramPreview } from './MiniDiagramPreview';

import { workflowRepo } from '@/lib/api-services';

import type {
  ChatMessage as ChatMessageType,
  MessageResponse,
  PhaseSummary,
  WorkflowState,
} from '@/shared/workflowAITypes';

// ── State reducer ──────────────────────────────────────────────────────────

type Action =
  | { type: 'ADD_USER_MESSAGE'; text: string }
  | { type: 'ADD_AI_MESSAGE'; text: string; response: MessageResponse }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_WORKFLOW_ID'; id: string }
  | { type: 'SET_PHASES'; phases: PhaseSummary[] }
  | { type: 'SET_ACTIVE_PHASE'; id: string | null }
  | { type: 'MARK_STALE'; phaseIds: string[] }
  | { type: 'SET_INSTANT_MESSAGE'; text: string }
  | { type: 'RESET' };

function reducer(state: WorkflowState, action: Action): WorkflowState {
  switch (action.type) {
    case 'ADD_USER_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            role: 'user',
            text: action.text,
            timestamp: new Date(),
          },
        ],
      };
    case 'ADD_AI_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: action.text,
            timestamp: new Date(),
            response: action.response,
          },
        ],
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };
    case 'SET_WORKFLOW_ID':
      return { ...state, workflowId: action.id };
    case 'SET_PHASES':
      return { ...state, phases: action.phases };
    case 'SET_ACTIVE_PHASE':
      return { ...state, activePhaseId: action.id };
    case 'MARK_STALE':
      return {
        ...state,
        phases: state.phases.map(p =>
          action.phaseIds.includes(p.id) ? { ...p, status: 'stale' as const } : p,
        ),
      };
    case 'SET_INSTANT_MESSAGE':
      return { ...state, instantMessage: action.text };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const initialState: WorkflowState = {
  workflowId: null,
  activePhaseId: null,
  phases: [],
  messages: [],
  isLoading: false,
};

const SESSION_KEY = 'ai_workflow_state';

function initWorkflowState(initial: WorkflowState): WorkflowState {
  if (typeof window === 'undefined') return initial;

  try {
    // If this is the first time WorkflowChat mounts since the page loaded,
    // we want to clear the session. This ensures a hard refresh clears the session,
    // while subsequent client-side navigations preserve it.
    if (!(window as any).__AI_SESSION_INITIALIZED__) {
      sessionStorage.removeItem(SESSION_KEY);
      (window as any).__AI_SESSION_INITIALIZED__ = true;
      return initial;
    }

    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Revive timestamps
      const messages = (parsed.messages || []).map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp)
      }));
      return { ...initial, ...parsed, messages, isLoading: false };
    }
  } catch (err) {
    console.error("Failed to load session state", err);
  }
  return initial;
}

// ── Main component ─────────────────────────────────────────────────────────

export function WorkflowChat() {
  const [state, dispatch] = useReducer(reducer, initialState, initWorkflowState);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [previewWfJson, setPreviewWfJson] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Sync state to session storage to preserve session across page navigation
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  }, [state]);

  const hasInitialData = useRef(!!(state.instantMessage || state.messages.length > 0));

  // Initial fetch for instantMessage
  useEffect(() => {
    if (hasInitialData.current) return;

    let isMounted = true;
    async function fetchInitial() {
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const res = await workflowRepo.chatWorkflowAPI({});
        if (isMounted && res.instantMessage) {
          dispatch({ type: 'SET_INSTANT_MESSAGE', text: res.instantMessage });
        }
      } catch (err) {
        console.error("Failed to fetch initial workflow message:", err);
      } finally {
        if (isMounted) dispatch({ type: 'SET_LOADING', loading: false });
      }
    }
    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages]);

  // ── Handle API response (the single switch from the contract) ────────
  const handleResponse = useCallback(
    (res: MessageResponse) => {
      // Always-present fields
      dispatch({ type: 'ADD_AI_MESSAGE', text: res.assistantReply, response: res });
      dispatch({ type: 'SET_ACTIVE_PHASE', id: res.activePhaseId });

      if (res.workflowId) dispatch({ type: 'SET_WORKFLOW_ID', id: res.workflowId });
      if (res.phases) dispatch({ type: 'SET_PHASES', phases: res.phases });
      if (res.staleDownstream?.length) dispatch({ type: 'MARK_STALE', phaseIds: res.staleDownstream });
    },
    [],
  );

  // ── Send message ─────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      dispatch({ type: 'ADD_USER_MESSAGE', text });
      dispatch({ type: 'SET_LOADING', loading: true });

      try {
        const res = await workflowRepo.chatWorkflowAPI({
          message: text,
          workflowId: state.workflowId,
        });
        handleResponse(res);
      } catch {
        dispatch({
          type: 'ADD_AI_MESSAGE',
          text: 'Something went wrong. Please try again.',
          response: {
            actionTaken: 'error',
            assistantReply: 'Something went wrong. Please try again.',
            workflowId: state.workflowId || '',
            activePhaseId: state.activePhaseId,
            error: { code: 'UNKNOWN', message: 'Unexpected error' },
          },
        });
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [state.workflowId, state.activePhaseId, handleResponse],
  );

  // ── Phase selection handler ──────────────────────────────────────────
  const handlePhaseSelection = useCallback(
    async (selectedIds: string[]) => {
      const text = `I'll use these phases: ${selectedIds.join(', ')}`;
      dispatch({ type: 'ADD_USER_MESSAGE', text });
      dispatch({ type: 'SET_LOADING', loading: true });

      try {
        const res = await workflowRepo.chatWorkflowAPI({
          workflowId: state.workflowId,
          selectedPhaseIds: selectedIds,
        });
        handleResponse(res);
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [state.workflowId, handleResponse],
  );

  // ── Question answer handler ──────────────────────────────────────────
  const handleQuestionAnswer = useCallback(
    async (answers: Record<string, string | string[] | boolean>) => {
      const summary = Object.values(answers)
        .map(v => (Array.isArray(v) ? v.join(', ') : String(v)))
        .join(' · ');
      const text = `My answers: ${summary}`;
      dispatch({ type: 'ADD_USER_MESSAGE', text });
      dispatch({ type: 'SET_LOADING', loading: true });

      try {
        const res = await workflowRepo.chatWorkflowAPI({
          workflowId: state.workflowId,
          questionAnswers: answers,
        });
        handleResponse(res);
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [state.workflowId, handleResponse],
  );

  // ── Template selection handler ───────────────────────────────────────
  const handleTemplateSelect = useCallback(
    async (templateId: string) => {
      const text = `I'll go with template ${templateId}`;
      dispatch({ type: 'ADD_USER_MESSAGE', text });
      dispatch({ type: 'SET_LOADING', loading: true });

      try {
        const res = await workflowRepo.chatWorkflowAPI({
          workflowId: state.workflowId,
          selectedTemplateId: templateId,
        });
        handleResponse(res);
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [state.workflowId, handleResponse],
  );

  // ── Reset ────────────────────────────────────────────────────────────
  const handleReset = () => {
    dispatch({ type: 'RESET' });
    // Re-fetch initial on reset
    dispatch({ type: 'SET_LOADING', loading: true });
    workflowRepo.chatWorkflowAPI({}).then((res: any) => {
      if (res.instantMessage) {
        dispatch({ type: 'SET_INSTANT_MESSAGE', text: res.instantMessage });
      }
    }).catch(console.error).finally(() => {
      dispatch({ type: 'SET_LOADING', loading: false });
    });
  };

  // ── Determine interactive block per message ──────────────────────────
  const renderInteractiveBlock = (msg: ChatMessageType, isLast: boolean) => {
    if (msg.role !== 'assistant' || !msg.response) return null;
    const res = msg.response;

    // Only make the latest interactive block actionable
    const disabled = !isLast || state.isLoading;

    switch (res.actionTaken) {
      case 'start_new_workflow':
        if (res.phaseOptions) {
          return (
            <PhaseOptionPicker
              options={res.phaseOptions}
              onConfirm={handlePhaseSelection}
              disabled={disabled}
            />
          );
        }
        break;

      case 'phases_selected':
      case 'questions_required':
      case 'clarifying_answer':
      case 'phase_edited':
        if (res.questions?.length) {
          return (
            <QuestionForm
              questions={res.questions}
              onSubmit={handleQuestionAnswer}
              disabled={disabled}
            />
          );
        }
        break;

      case 'templates_generated':
        if (res.templateOptions?.length) {
          return (
            <TemplateCarousel
              templates={res.templateOptions}
              onSelect={handleTemplateSelect}
              disabled={disabled}
            />
          );
        }
        break;

      case 'template_selected':
        // If this message also has questions for the next phase, show them
        if (res.questions?.length) {
          return (
            <QuestionForm
              questions={res.questions}
              onSubmit={handleQuestionAnswer}
              disabled={disabled}
            />
          );
        }
        break;

      case 'workflow_completed': {
        let preview = null;
        if (res.finalDiagram) {
          try {
            const allNodes = res.finalDiagram.phases.flatMap(p =>
              (p.diagramSpec?.nodes || []).map(n => ({
                ...n,
                data: { label: n.label, ...n.data }
              }))
            );
            const allEdges = [
              ...res.finalDiagram.phases.flatMap(p => p.diagramSpec?.edges || []),
              ...(res.finalDiagram.crossPhaseEdges || [])
            ];
            const combinedWfJson = JSON.stringify({ nodes: allNodes, edges: allEdges });

            if (allNodes.length > 0) {
              preview = (
                <div className="mt-4 border-t border-emerald-200/60 pt-4 w-full">
                  <div className="flex items-center justify-center mb-2">
                    {/* <p className="text-xs font-semibold text-emerald-800 text-left">Generated Diagram Preview</p> */}
                    <Button variant="outline" size="sm" onClick={() => window.open(`https://workflow-designer.workfinity.com/workflow/${res.savedWorkflowId || state.workflowId}`, '_blank', 'noopener,noreferrer')} className="h-7 text-xs px-4 border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                      <Eye className="h-3 w-3 mr-1" /> Preview Mode
                    </Button>
                  </div>
                  {/* <MiniDiagramPreview wfJson={combinedWfJson} height={200} className="border-emerald-200/50" /> */}
                </div>
              );
            }
          } catch (err) {
            console.error("Error creating combined preview diagram", err);
          }
        }

        return (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 text-center flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Workflow className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-emerald-800">Workflow Complete!</p>
            <p className="text-xs text-emerald-600 mb-2">
              {state.phases.length} phases configured successfully
            </p>
            {preview}
          </div>
        );
      }

      case 'error':
        return (
          <div className="rounded-xl border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">
            <p className="font-medium">Error: {res.error?.code}</p>
            <p className="text-xs text-red-500 mt-0.5">{res.error?.message}</p>
          </div>
        );
    }

    return null;
  };

  // Determine if text input should be disabled because the user must interact with UI
  // Input is disabled:
  // 1) On the initial step (user must click initial prompt suggestion pill)
  // 2) At the phase/application selection step (user must select workflow phases from the phase picker UI)
  // Prompting is enabled only AFTER phase selection is completed.
  const lastMessage = state.messages[state.messages.length - 1];
  const isPhaseSelectionStep =
    lastMessage?.role === 'assistant' &&
    (lastMessage?.response?.actionTaken === 'start_new_workflow' || !!lastMessage?.response?.phaseOptions);

  const isInputDisabled = state.messages.length === 0 || isPhaseSelectionStep;

  let inputPlaceholder = 'Describe your workflow or ask a question...';
  if (state.messages.length === 0) {
    inputPlaceholder = 'Please click an initial prompt above to start...';
  } else if (isPhaseSelectionStep) {
    inputPlaceholder = 'Please select workflow phases above to continue...';
  }

  return (
    <div className="flex h-[calc(100vh-120px)] bg-gradient-to-br from-slate-50 via-white to-workfinity-50/30 rounded-xl border border-slate-200/80 overflow-hidden shadow-sm relative">
      {/* ── Chat panel ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 w-full overflow-hidden relative">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 bg-white/70 backdrop-blur-sm">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-workfinity-500 to-workfinity-600 flex items-center justify-center shadow-sm">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-800">AI Workflow Builder</h2>
              <p className="text-[11px] text-slate-400">
                {state.workflowId
                  ? `Workflow ${state.workflowId}`
                  : 'Describe your workflow to get started'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {state.messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-slate-400 hover:text-slate-600 h-8 px-2"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">Reset</span>
              </Button>
            )}
            {state.phases.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0"
              >
                {sidebarOpen ? (
                  <PanelRightClose className="h-4 w-4" />
                ) : (
                  <PanelRightOpen className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Messages area */}
        <ScrollArea className="flex-1 min-h-0 w-full overflow-x-hidden">
          <div className="p-3 sm:p-4 space-y-5 w-full max-w-full overflow-x-hidden">
            {/* Empty state */}
            {state.messages.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center justify-center py-16 text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-workfinity-100 to-workfinity-200 flex items-center justify-center shadow-sm">
                  <Sparkles className="h-8 w-8 text-workfinity-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Build Your Workflow
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm leading-relaxed">
                    Tell me what workflow you'd like to create and I'll guide you through
                    selecting phases, answering configuration questions, and choosing templates.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center mt-2 min-h-[40px]">
                  {state.isLoading && !state.instantMessage ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading suggestions...</span>
                    </div>
                  ) : (
                    (state.instantMessage ? [state.instantMessage] : [
                      'Create a hiring workflow',
                      'Build an onboarding process',
                      'Design an approval workflow',
                    ]).map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => sendMessage(suggestion)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:border-workfinity-300 hover:bg-workfinity-50 hover:text-workfinity-700 transition-all duration-200 shadow-xs"
                      >
                        {suggestion}
                      </button>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {/* Chat messages */}
            {state.messages.map((msg, idx) => {
              const isLast = idx === state.messages.length - 1;
              return (
                <ChatMessage key={msg.id} message={msg}>
                  {renderInteractiveBlock(msg, isLast)}
                </ChatMessage>
              );
            })}

            {/* Typing indicator */}
            <AnimatePresence>
              {state.isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200/60 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-workfinity-500" />
                  </div>
                  <div className="flex gap-1 px-4 py-3 rounded-2xl rounded-bl-md bg-white/80 backdrop-blur-sm border border-slate-200/60 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-workfinity-400 animate-bounce [animation-delay:0ms]" />
                    <span className="w-2 h-2 rounded-full bg-workfinity-400 animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 rounded-full bg-workfinity-400 animate-bounce [animation-delay:300ms]" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input */}
        <ChatInput onSend={sendMessage} isLoading={state.isLoading} disabled={isInputDisabled} placeholder={inputPlaceholder} />
      </div>

      {/* ── Phase tracker sidebar ──────────────────────────────────────── */}
      <AnimatePresence>
        {sidebarOpen && state.phases.length > 0 && (
          <>
            {/* Mobile Drawer Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 md:hidden"
            />

            {/* Mobile Slide-Over Drawer */}
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-72 max-w-[85vw] bg-white border-l border-slate-200 shadow-2xl flex flex-col md:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <Workflow className="h-4 w-4 text-workfinity-600" />
                  <h3 className="text-sm font-semibold text-slate-800">Workflow Progress</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(false)}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <ScrollArea className="flex-1 p-4">
                <PhaseTracker
                  phases={state.phases}
                  activePhaseId={state.activePhaseId}
                />
              </ScrollArea>
            </motion.aside>

            {/* Desktop Inline Panel */}
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 240, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="hidden md:flex flex-col border-l border-slate-200 bg-white overflow-hidden flex-shrink-0 z-10"
            >
              <ScrollArea className="h-full">
                <div className="p-4">
                  <PhaseTracker
                    phases={state.phases}
                    activePhaseId={state.activePhaseId}
                  />
                </div>
              </ScrollArea>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Zoomable Preview Dialog (Commented for future reuse)
      <Dialog open={!!previewWfJson} onOpenChange={(open) => !open && setPreviewWfJson(null)}>
        <DialogContent minWidth="sm:min-w-[850px]" maxWidth="max-w-4xl" className="p-6">
          {previewWfJson && (
            <>
              <DialogHeader className="mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-workfinity-100 to-workfinity-200 flex items-center justify-center">
                    <Workflow className="h-5 w-5 text-workfinity-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-bold text-slate-900">
                      Completed Workflow Diagram
                    </DialogTitle>
                    <DialogDescription className="text-sm text-slate-500 mt-0.5">
                      Detailed view of the generated phases and tasks.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              
              <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg p-2 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 px-2 uppercase tracking-wider">
                    Zoom Control
                  </span>
                  <div className="h-4 w-[1px] bg-slate-200" />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                    disabled={zoom <= 0.5}
                    className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-xs font-medium text-slate-600 min-w-[3rem] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                    disabled={zoom >= 3}
                    className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setZoom(1)}
                    disabled={zoom === 1}
                    className="h-8 px-2 text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 pr-2">
                  <span>Scroll inside to explore</span>
                </div>
              </div>

              
              <div className="w-full h-[500px] overflow-auto border border-slate-200/80 rounded-xl bg-gradient-to-br from-slate-50/50 to-white/50 shadow-inner relative">
                <div
                  className={cn(
                    "transition-all duration-200 ease-out flex",
                    zoom > 1 ? "items-start justify-start p-8" : "items-center justify-center p-6"
                  )}
                  style={{
                    width: `${100 * zoom}%`,
                    height: `${100 * zoom}%`,
                    minWidth: '100%',
                    minHeight: '100%',
                  }}
                >
                  <div
                    className="w-full"
                    style={{
                      maxWidth: `${768 * zoom}px`,
                    }}
                  >
                    <MiniDiagramPreview
                      wfJson={previewWfJson}
                      height={450 * zoom}
                      className="w-full bg-transparent border-none shadow-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                <Button variant="outline" onClick={() => setPreviewWfJson(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
}
